import { useEffect, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/LoadingError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { apiFetch } from "@/utils/api";

interface ReceiptOptions {
  vendorSources: string[];
  requireReceivedBy: boolean;
  requireSerialNotes: boolean;
}

export function AdminReceiptOptionsPage() {
  const [options, setOptions] = useState<ReceiptOptions>({
    vendorSources: [],
    requireReceivedBy: false,
    requireSerialNotes: false,
  });
  const [newVendor, setNewVendor] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch<{ options: ReceiptOptions }>("/admin/receipt-options")
      .then((d) => setOptions(d.options))
      .catch((e) => setError(e instanceof Error ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const data = await apiFetch<{ options: ReceiptOptions }>("/admin/receipt-options", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(options),
      });
      setOptions(data.options);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addVendor = () => {
    const name = newVendor.trim();
    if (!name || options.vendorSources.includes(name)) return;
    setOptions({ ...options, vendorSources: [...options.vendorSources, name] });
    setNewVendor("");
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Receiving Options"
        subtitle="Configure defaults and requirements for receiving."
      />
      <div className="bg-white rounded-lg shadow p-6 space-y-6 max-w-lg">
        <div className="space-y-3">
          <Label>Vendor / source presets</Label>
          <p className="text-xs text-muted-foreground">
            Shown as quick picks when creating a receiving record.
          </p>
          <div className="flex flex-wrap gap-2">
            {options.vendorSources.map((vendor) => (
              <span
                key={vendor}
                className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-sm"
              >
                {vendor}
                <button
                  type="button"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() =>
                    setOptions({
                      ...options,
                      vendorSources: options.vendorSources.filter((v) => v !== vendor),
                    })
                  }
                  aria-label={`Remove ${vendor}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newVendor}
              onChange={(e) => setNewVendor(e.target.value)}
              placeholder="Add vendor name"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addVendor();
                }
              }}
            />
            <Button type="button" variant="secondary" onClick={addVendor}>
              Add
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="require-received-by">Require received by</Label>
          <Switch
            id="require-received-by"
            checked={options.requireReceivedBy}
            onCheckedChange={(checked) => setOptions({ ...options, requireReceivedBy: checked })}
          />
        </div>

        <div className="flex items-center justify-between">
          <Label htmlFor="require-serial-notes">Require serial notes on line items</Label>
          <Switch
            id="require-serial-notes"
            checked={options.requireSerialNotes}
            onCheckedChange={(checked) => setOptions({ ...options, requireSerialNotes: checked })}
          />
        </div>

        {error && <p className="text-sm text-destructive">{error}</p>}
        <Button
          onClick={() => void save()}
          disabled={saving}
          className="bg-[#1e3a4f] hover:bg-[#163041]"
        >
          {saving ? "Saving…" : "Save"}
        </Button>
        {saved && <p className="text-sm text-green-700">Settings saved.</p>}
      </div>
    </div>
  );
}
